/**
 * Tibbs's per-device settings (docs/TIBBS-VOICE.md 2). Pure functions, like
 * Single Didge's audio-settings.js; the caller does the storage.
 *
 * Stored as `ancientnine.tibbs` = { v: 2, vol, captions }. Tibbs only speaks
 * when someone asks, so there is no on/off or "how chatty" switch: his volume
 * (0 to 100, separate from the sound effects) and captions are all there is.
 * The top-bar sound switch still silences him (captions carry on).
 */
export const TIBBS_SETTINGS_VERSION = 2;

export interface TibbsSettings {
  v: 2;
  /** 0..100; 100 is the rendered loudness. */
  vol: number;
  captions: boolean;
}

export function defaultTibbsSettings(): TibbsSettings {
  return { v: TIBBS_SETTINGS_VERSION, vol: 85, captions: true };
}

export const clampVol = (v: unknown): number => {
  const n = Number(v);
  return Number.isFinite(n) ? Math.max(0, Math.min(100, Math.round(n))) : defaultTibbsSettings().vol;
};

/** Read whatever was stored (anything, including junk or the v1 design shape) into current settings. */
export function normalizeTibbsSettings(raw: unknown): TibbsSettings {
  const def = defaultTibbsSettings();
  if (!raw || typeof raw !== 'object') return def;
  const o = raw as Record<string, unknown>;
  return {
    v: TIBBS_SETTINGS_VERSION,
    vol: o.vol === undefined ? def.vol : clampVol(o.vol),
    captions: typeof o.captions === 'boolean' ? o.captions : def.captions,
  };
}

/** Linear gain for the voice: volume and the game's mute switch. */
export function voiceGain(s: TibbsSettings, muted: boolean): number {
  if (muted) return 0;
  const x = s.vol / 100;
  // A gentle curve so the slider's middle is not too quiet.
  return x * x * 0.4 + x * 0.6;
}

export function withVolume(s: TibbsSettings, vol: number): TibbsSettings {
  return { ...s, vol: clampVol(vol) };
}

export function withCaptions(s: TibbsSettings, captions: boolean): TibbsSettings {
  return { ...s, captions };
}
